import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import WpaLayout from './WpaLayout';
import { formatWpaDate, getWpaCompanyTimezone } from './wpaDateUtils';

const PRIMARY = '#4CAF50';

function getTabs(t) {
  return [
    { key: '', label: t('wpa.orders.tabAll') },
    { key: 'submitted', label: t('wpa.orders.tabSubmitted') },
    { key: 'processing', label: t('wpa.orders.tabProcessing') },
  ];
}

/** 當訂單已被第三方讀入（isReadBy3psys）且仍為 submitted 時，視覺上呈現為 processing */
function resolveDisplayStatus(order) {
  if (!order) return '';
  const { status, isReadBy3psys } = order;
  if (status === 'submitted' && isReadBy3psys) return 'processing';
  return status || '';
}

function orderStatusLabel(t, order) {
  const displayStatus = typeof order === 'string' ? order : resolveDisplayStatus(order);
  const map = {
    pending: () => t('wpa.orders.statusPending'),
    submitted: () => t('wpa.orders.statusSubmitted'),
    processing: () => t('wpa.orders.statusProcessing'),
    completed: () => t('wpa.orders.statusCompleted'),
    cancelled: () => t('wpa.orders.statusCancelled'),
  };
  return map[displayStatus]?.() || displayStatus;
}

function getStatusStyle(status) {
  switch (status) {
    case 'pending':
      return { background: '#fff3e0', color: '#f57c00' };
    case 'submitted':
      return { background: '#e3f2fd', color: '#1976d2' };
    case 'processing':
      return { background: '#f3e5f5', color: '#7b1fa2' };
    case 'completed':
      return { background: '#e8f5e9', color: '#388e3c' };
    case 'cancelled':
      return { background: '#fbe9e7', color: '#d32f2f' };
    default:
      return { background: '#f5f5f5', color: '#666' };
  }
}

const styles = {
  tabBar: {
    display: 'flex',
    borderBottom: '2px solid #e0e0e0',
    background: '#fff',
    position: 'sticky',
    top: 56,
    zIndex: 100,
  },
  tab: {
    flex: 1,
    padding: '12px 0',
    textAlign: 'center',
    fontSize: 14,
    fontWeight: 500,
    color: '#999',
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    marginBottom: -2,
    transition: 'color 0.2s, border-color 0.2s',
  },
  tabActive: {
    color: PRIMARY,
    fontWeight: 600,
    borderBottomColor: PRIMARY,
  },
  list: {
    padding: '8px 0 20px',
  },
  orderRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '14px 16px',
    borderBottom: '1px solid #f0f0f0',
    cursor: 'pointer',
    gap: 12,
  },
  orderInfo: {
    flex: 1,
    minWidth: 0,
  },
  orderNo: {
    fontSize: 15,
    fontWeight: 600,
    color: '#333',
  },
  orderDate: {
    fontSize: 13,
    color: '#999',
    marginTop: 2,
  },
  orderRight: {
    textAlign: 'right',
    flexShrink: 0,
  },
  orderAmount: {
    fontSize: 16,
    fontWeight: 700,
    color: PRIMARY,
  },
  statusBadge: {
    display: 'inline-block',
    fontSize: 11,
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 10,
    marginTop: 4,
  },
  chevron: {
    fontSize: 20,
    color: '#ccc',
    flexShrink: 0,
  },
  loading: {
    textAlign: 'center',
    padding: 32,
    color: '#999',
  },
  loadMore: {
    textAlign: 'center',
    padding: 16,
  },
  loadMoreBtn: {
    padding: '10px 32px',
    background: '#f5f5f5',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    fontSize: 14,
    color: '#666',
    cursor: 'pointer',
  },
};

const PAGE_SIZE = 20;

export default function WpaOrdersPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const tabs = useMemo(() => getTabs(t), [t]);
  const [activeTab, setActiveTab] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchOrders = useCallback(async (pageNum, append = false) => {
    const token = localStorage.getItem('wpa_token');
    const params = new URLSearchParams();
    if (activeTab) params.set('status', activeTab);
    params.set('page', pageNum);
    params.set('pageSize', PAGE_SIZE);

    try {
      if (!append) setLoading(true);
      else setLoadingMore(true);

      const res = await fetch(`/api/wpa-commerce/orders?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        navigate(`/${companyId}/wpa/login`, { replace: true });
        return;
      }
      const json = await res.json();
      const raw = json.data || json.items || json;
      const items = Array.isArray(raw) ? raw : [];
      setHasMore(items.length >= PAGE_SIZE);

      if (append) {
        setOrders((prev) => [...prev, ...items]);
      } else {
        setOrders(items);
      }
    } catch (err) {
      console.error('Failed to fetch orders', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeTab, navigate, companyId]);

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    fetchOrders(1, false);
  }, [activeTab, fetchOrders]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchOrders(nextPage, true);
  };

  return (
    <WpaLayout title={t('wpa.titles.orders')}>
      <div style={styles.tabBar}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            style={{
              ...styles.tab,
              ...(activeTab === tab.key ? styles.tabActive : {}),
            }}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={styles.list}>
        {loading && <div style={styles.loading}>{t('wpa.common.loading')}</div>}
        {!loading && orders.length === 0 && (
          <div style={styles.loading}>{t('wpa.common.noOrders')}</div>
        )}
        {orders.map((order) => (
          <div
            key={order.id}
            style={styles.orderRow}
            onClick={() => navigate(`/${companyId}/wpa/orders/${order.id}`)}
          >
            <div style={styles.orderInfo}>
              <div style={styles.orderNo}>{order.orderNo}</div>
              {(order.customerNo || order.branchNo) && (
                <div style={styles.orderDate}>
                  {[order.customerNo, order.branchNo].filter(Boolean).join(' - ')}
                </div>
              )}
              <div style={styles.orderDate}>
                {formatWpaDate(order.deliveryDate, getWpaCompanyTimezone(), 'YYYY-MM-DD')}
              </div>
            </div>
            <div style={styles.orderRight}>
              <div style={styles.orderAmount} translate="no">
                ${Number(order.totalAmount || 0).toFixed(2)}
              </div>
              <span style={{ ...styles.statusBadge, ...getStatusStyle(resolveDisplayStatus(order)) }}>
                {orderStatusLabel(t, order)}
              </span>
            </div>
            <span style={styles.chevron}>›</span>
          </div>
        ))}
        {!loading && hasMore && orders.length > 0 && (
          <div style={styles.loadMore}>
            <button
              type="button"
              style={styles.loadMoreBtn}
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? t('wpa.common.loading') : t('wpa.common.loadMore')}
            </button>
          </div>
        )}
      </div>
    </WpaLayout>
  );
}
